import axios from 'axios';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import useOidcStore from '@shop/store/oidc';
import { generateUrl } from '@shop/utils/Routing';

interface WishlistProduct {
  entity_id: number;
}

interface WishlistResponse {
  products?: WishlistProduct[];
}

interface AppState {
  Wishlist?: {
    wishList?: number[];
    isSynced?: boolean;
  };
  [key: string]: unknown;
}

const useWishlistStore = defineStore(
  'wishlist',
  () => {
    const wishList = ref<number[]>([]);
    const isSynced = ref<boolean>(false);
    const isSyncingFromStorage = ref<boolean>(false);

    const itemCount = computed(() => wishList.value.length);

    const isLoggedIn = computed(() => {
      const oidcStore = useOidcStore();
      return oidcStore.oidcIsAuthenticated;
    });

    const localList = computed(() => wishList.value);

    const isSyncedWithFrontEnd = computed(() => isSynced.value);

    function setLocalList(wishlist: number[]) {
      wishList.value = wishlist;
    }

    function setIsSynced(status: boolean) {
      isSynced.value = status;
    }

    function addItemLocal(productId: number) {
      if (!wishList.value.includes(productId)) {
        wishList.value = [...wishList.value, productId];
      }
    }

    function removeItemLocal(productId: number) {
      wishList.value = wishList.value.filter(item => item !== productId);
    }

    async function syncFromFrontend(): Promise<void> {
      if (wishList.value.length > 0) {
        const response = await axios.post<WishlistResponse>(
          generateUrl('api_customer_wishlist_add'),
          { products: wishList.value }
        );
        const products = response.data?.products ?? [];
        const locallist = products.map(item => item.entity_id);
        setLocalList(locallist);
        setIsSynced(true);
      } else {
        const response = await axios.get<WishlistResponse>(
          generateUrl('api_customer_wishlist_index')
        );
        const products = response.data?.products ?? [];
        const wishlist = products.map(item => item.entity_id);
        setLocalList(wishlist);
        setIsSynced(true);
      }
    }

    async function addItem(productId: number): Promise<void> {
      const productIdNum = Number(productId);
      if (wishList.value.some(item => item === productIdNum)) {
        return;
      }

      if (isLoggedIn.value) {
        await axios.post<WishlistResponse>(generateUrl('api_customer_wishlist_add'), {
          products: [productIdNum],
        });
        addItemLocal(productIdNum);
      } else {
        addItemLocal(productIdNum);
        setIsSynced(false);
      }
    }

    function syncToStorage(): void {
      if (isSyncingFromStorage.value) {
        return;
      }

      try {
        const localAppState = localStorage.getItem('app');
        if (localAppState) {
          const appState = JSON.parse(localAppState) as AppState;
          appState.Wishlist ??= {};
          appState.Wishlist.wishList = wishList.value;
          appState.Wishlist.isSynced = isSynced.value;
          localStorage.setItem('app', JSON.stringify(appState));
        } else {
          const newAppState: AppState = {
            Wishlist: {
              wishList: wishList.value,
              isSynced: isSynced.value,
            },
          };
          localStorage.setItem('app', JSON.stringify(newAppState));
        }
      } catch {
        // Skip error
      }
    }

    async function removeItems(productIds: number[]): Promise<void> {
      if (isLoggedIn.value) {
        await axios.post<WishlistResponse>(generateUrl('api_customer_wishlist_remove'), {
          products: productIds,
        });
        productIds.forEach(productId => removeItemLocal(productId));
      } else {
        productIds.forEach(productId => removeItemLocal(productId));
        setIsSynced(false);
      }
      syncToStorage();
    }

    function arraysEqual(a: number[], b: number[]): boolean {
      if (a.length !== b.length) {
        return false;
      }
      const sortedA = [...a].sort((x, y) => x - y);
      const sortedB = [...b].sort((x, y) => x - y);
      return sortedA.every((val, index) => val === sortedB[index]);
    }

    function syncFromStorage(): void {
      isSyncingFromStorage.value = true;
      try {
        const localAppState = localStorage.getItem('app');
        if (localAppState) {
          const appState = JSON.parse(localAppState) as AppState;
          if (appState.Wishlist) {
            const newWishList = Array.isArray(appState.Wishlist.wishList)
              ? appState.Wishlist.wishList
              : [];
            const newIsSynced =
              typeof appState.Wishlist.isSynced === 'boolean' ? appState.Wishlist.isSynced : false;

            if (!arraysEqual(wishList.value, newWishList)) {
              wishList.value = newWishList;
            }
            if (isSynced.value !== newIsSynced) {
              isSynced.value = newIsSynced;
            }
            isSyncingFromStorage.value = false;
            return;
          }
        }

        const wishlistBadge = document.querySelector('.cartheader__wishlist-badge');
        if (wishlistBadge) {
          const text = wishlistBadge.textContent?.trim() ?? '0';
          const count = parseInt(text, 10);
          if (!Number.isNaN(count) && count >= 0 && wishList.value.length !== count) {
            if (count === 0) {
              wishList.value = [];
            } else {
              wishList.value = Array(count)
                .fill(0)
                .map((_, i) => i + 1);
            }
          }
        }
      } catch {
        const wishlistBadge = document.querySelector('.cartheader__wishlist-badge');
        if (wishlistBadge) {
          const text = wishlistBadge.textContent?.trim() ?? '0';
          const count = parseInt(text, 10);
          if (!Number.isNaN(count) && count >= 0 && wishList.value.length !== count) {
            if (count === 0) {
              wishList.value = [];
            } else {
              wishList.value = Array(count)
                .fill(0)
                .map((_, i) => i + 1);
            }
          }
        }
      } finally {
        isSyncingFromStorage.value = false;
      }
    }

    function setupStorageListener() {
      window.addEventListener('storage', event => {
        if (event.key === 'app') {
          syncFromStorage();
        }
      });

      let lastAppState: string | null = null;

      const checkAppState = (): void => {
        try {
          const currentAppState = localStorage.getItem('app');
          if (currentAppState !== lastAppState) {
            if (currentAppState) {
              lastAppState = currentAppState;
              const appState = JSON.parse(currentAppState) as AppState;
              if (appState.Wishlist) {
                syncFromStorage();
              }
            } else if (lastAppState !== null) {
              lastAppState = null;
              if (wishList.value.length > 0) {
                wishList.value = [];
                isSynced.value = false;
              }
            }
          }
        } catch {
          // Skip error
        }
      };

      const intervalId = setInterval(checkAppState, 500);

      const handleFocus = () => {
        syncFromStorage();
      };

      const handleVisibilityChange = () => {
        if (!document.hidden) {
          syncFromStorage();
        }
      };

      window.addEventListener('focus', handleFocus);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        clearInterval(intervalId);
        window.removeEventListener('focus', handleFocus);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }

    if (typeof window !== 'undefined') {
      setupStorageListener();

      watch(
        [wishList, isSynced],
        () => {
          syncToStorage();
        },
        { deep: true, immediate: false }
      );
    }

    syncFromStorage();

    return {
      wishList,
      isSynced,
      itemCount,
      isLoggedIn,
      localList,
      isSyncedWithFrontEnd,
      setLocalList,
      setIsSynced,
      addItemLocal,
      removeItemLocal,
      syncFromFrontend,
      addItem,
      removeItems,
      syncFromStorage,
      syncToStorage,
    };
  },
  {
    persist: {
      storage: localStorage,
      key: 'wishlist',
      pick: ['wishList', 'isSynced'],
      serializer: {
        deserialize: (value: string) => {
          try {
            let parsed: { wishList: number[]; isSynced: boolean } = {
              wishList: [],
              isSynced: false,
            };
            if (value) {
              parsed = JSON.parse(value) as { wishList: number[]; isSynced: boolean };
            }
            const localAppState = localStorage.getItem('app');
            if (localAppState) {
              const appState = JSON.parse(localAppState) as AppState;
              if (appState.Wishlist?.wishList && Array.isArray(appState.Wishlist.wishList)) {
                parsed.wishList = appState.Wishlist.wishList;
                if (typeof appState.Wishlist.isSynced === 'boolean') {
                  parsed.isSynced = appState.Wishlist.isSynced;
                }
              }
            }
            return parsed;
          } catch {
            const localAppState = localStorage.getItem('app');
            if (localAppState) {
              try {
                const appState = JSON.parse(localAppState) as AppState;
                if (appState.Wishlist?.wishList && Array.isArray(appState.Wishlist.wishList)) {
                  return {
                    wishList: appState.Wishlist.wishList,
                    isSynced:
                      typeof appState.Wishlist.isSynced === 'boolean'
                        ? appState.Wishlist.isSynced
                        : false,
                  };
                }
              } catch {
                // Skip error
              }
            }
            return { wishList: [], isSynced: false };
          }
        },
        serialize: JSON.stringify,
      },
    },
  }
);

export default useWishlistStore;
